import React, { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../graphql/mutations';
import useOperationalStore from '../platform/store/useOperationalStore';
import FullscreenBtn from '../components/FullscreenBtn';
import './ShopMapView.css';

const client = generateClient();

// ─── Bay layout (no building outline — just bay orientation) ─────────────────
// Normal bay: w=180 h=80. Bay 3 = 1.5× (w=270). Bays 6,7 portrait (front-entry).
// Bay 6 left edge = Bay 3 right edge (245+270=515) — they butt.

const BAYS = [
  { id: 'bay-1', number: '1', x: 245, y: 60,  w: 180, h: 80 },
  { id: 'bay-2', number: '2', x: 245, y: 150, w: 180, h: 80 },
  { id: 'bay-3', number: '3', x: 245, y: 265, w: 270, h: 80 },
  { id: 'bay-4', number: '4', x: 475, y: 60,  w: 180, h: 80 },
  { id: 'bay-5', number: '5', x: 475, y: 150, w: 180, h: 80 },
  { id: 'bay-6', number: '6', x: 425, y: 350, w: 90,  h: 130 }, // 5px gap below bay-3; right edges align → L shape
  { id: 'bay-7', number: '7', x: 425, y: 485, w: 90,  h: 70  },
];

const YARD_COLS  = 3;
const YARD_ROWS  = 15;
const YARD_SLOTS = YARD_COLS * YARD_ROWS; // 45 total

// ─── Bay rect ─────────────────────────────────────────────────────────────────

function BayRect({ bay, trucks, isSelected, isDragOver, onSelect, onDragOver, onDragLeave, onDrop }) {
  const bayTrucks = trucks.filter(t => t.bayId === bay.id);
  const occupied  = bayTrucks.length > 0;

  const fill   = isDragOver ? '#dbeafe' : isSelected ? '#eff6ff' : occupied ? '#fca5a5' : '#ffffff';
  const stroke = isDragOver ? '#2563eb' : isSelected ? '#0f172a' : occupied ? '#dc2626' : '#94a3b8';
  const sw     = isDragOver || isSelected ? 3 : occupied ? 2 : 1.5;

  return (
    <g className="smap-bay-group" style={{ cursor: 'pointer' }}
      onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      onClick={() => onSelect(bay)}
    >
      <rect x={bay.x} y={bay.y} width={bay.w} height={bay.h}
        rx="3" fill={fill} stroke={stroke} strokeWidth={sw} />

      {/* Bay number */}
      <text x={bay.x + 8} y={bay.y + 18} fontSize="14" fontWeight="800" fill="#0f172a"
        fontFamily="ui-sans-serif, system-ui, sans-serif">{bay.number}</text>

      {isDragOver && (
        <text x={bay.x + bay.w / 2} y={bay.y + bay.h / 2 + 5}
          fontSize="11" fontWeight="700" fill="#3b82f6" textAnchor="middle"
          fontFamily="ui-sans-serif, system-ui, sans-serif">Drop here</text>
      )}

      {!isDragOver && bayTrucks[0] && (
        <>
          <text x={bay.x + 8} y={bay.y + 38} fontSize="11" fontWeight="700" fill="#dc2626"
            fontFamily="ui-sans-serif, system-ui, sans-serif">{bayTrucks[0].repairOrder}</text>
          <text x={bay.x + 8} y={bay.y + 54} fontSize="10" fill="#475569"
            fontFamily="ui-sans-serif, system-ui, sans-serif">
            {bayTrucks[0].truckNumber} {bayTrucks[0].companyName?.slice(0, 14)}
          </text>
        </>
      )}

      {bayTrucks.length > 1 && (
        <g>
          <circle cx={bay.x + bay.w - 12} cy={bay.y + 12} r="10" fill="#dc2626" />
          <text x={bay.x + bay.w - 12} y={bay.y + 16} fontSize="10" fontWeight="700"
            fill="#fff" textAnchor="middle" fontFamily="ui-sans-serif, system-ui, sans-serif">
            {bayTrucks.length}
          </text>
        </g>
      )}
    </g>
  );
}

// ─── Left yard strip ──────────────────────────────────────────────────────────

function YardStrip({ trucks, onDragStart, onDragEnd, onDropFromBay }) {
  return (
    <div
      className="smap-yard"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const truckId = e.dataTransfer.getData('text/plain');
        if (truckId) onDropFromBay(truckId);
      }}
    >
      <div className="smap-yard-title">Yard ({trucks.length}/{YARD_SLOTS})</div>
      <div className="smap-yard-grid">
        {Array.from({ length: YARD_SLOTS }).map((_, i) => {
          const truck = trucks[i];
          return (
            <div
              key={i}
              className={`smap-yard-slot${truck ? ' smap-yard-slot--occ' : ' smap-yard-slot--empty'}`}
              draggable={!!truck}
              onDragStart={truck ? (e) => onDragStart(e, truck.id) : undefined}
              onDragEnd={truck ? onDragEnd : undefined}
            >
              <span className="smap-yard-num">{i + 1}</span>
              {truck ? (
                <div className="smap-yard-info">
                  <span className="smap-yard-ro">{truck.repairOrder}</span>
                  {truck.truckNumber && <span className="smap-yard-unit">{truck.truckNumber}</span>}
                </div>
              ) : (
                <span className="smap-yard-empty-lbl">—</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Bay detail panel ─────────────────────────────────────────────────────────

function BayPanel({ bay, trucks, onClose, onUnassign, onDragStart, onDragEnd }) {
  const setSelectedTruckId = useOperationalStore(s => s.setSelectedTruckId);
  const bayTrucks = trucks.filter(t => t.bayId === bay.id);

  return (
    <div className="smap-bay-panel">
      <div className="smap-bp-header">
        <span className="smap-bp-title">Bay {bay.number}</span>
        <button className="smap-bp-close" onClick={onClose}>✕</button>
      </div>
      {bayTrucks.length === 0 ? (
        <div className="smap-bp-empty">No trucks assigned<br/><span style={{fontSize:10,color:'#cbd5e1'}}>Drop a truck here from the yard</span></div>
      ) : (
        <div className="smap-bp-list">
          {bayTrucks.map(t => (
            <div key={t.id} className="smap-bp-row"
              draggable
              onDragStart={(e) => onDragStart(e, t.id)}
              onDragEnd={onDragEnd}
            >
              <div className="smap-bp-info" style={{ cursor: 'pointer' }}
                onClick={() => setSelectedTruckId(t.id)}>
                <span className="smap-bp-ro">{t.repairOrder}</span>
                <span className="smap-bp-unit">{t.truckNumber} — {t.companyName}</span>
                <span className="smap-bp-status">{t.status}</span>
              </div>
              <button className="smap-bp-unassign" onClick={() => onUnassign(t.id)}>✕</button>
            </div>
          ))}
        </div>
      )}
      <div className="smap-bp-hint">Click to view · Drag to yard · ✕ to unassign</div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ShopMapView() {
  const trucks           = useOperationalStore(s => s.trucks);
  const localUpdateTruck = useOperationalStore(s => s.localUpdateTruck);

  const [selectedBay,   setSelectedBay]   = useState(null);
  const [dragOverBayId, setDragOverBayId] = useState(null);

  const activeTrucks = trucks.filter(t => !['completed', 'voided'].includes(t.status));
  const unplaced     = activeTrucks.filter(t => !t.bayId);
  const placed       = activeTrucks.filter(t => !!t.bayId);
  const baysOccupied = new Set(placed.map(t => t.bayId)).size;

  const handleDragStart = useCallback((e, truckId) => {
    e.dataTransfer.setData('text/plain', truckId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragEnd = useCallback(() => setDragOverBayId(null), []);

  const handleBayDragOver = useCallback((e, bayId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverBayId(bayId);
  }, []);

  const handleBayDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOverBayId(null);
  }, []);

  const handleBayDrop = useCallback(async (e, bay) => {
    e.preventDefault();
    setDragOverBayId(null);
    const truckId = e.dataTransfer.getData('text/plain');
    if (!truckId) return;
    localUpdateTruck(truckId, { bayId: bay.id });
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truckId, bayId: bay.id } } });
    } catch (err) { console.error('Bay assign failed:', err); }
  }, [localUpdateTruck]);

  const handleUnassign = useCallback(async (truckId) => {
    localUpdateTruck(truckId, { bayId: null });
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truckId, bayId: null } } });
    } catch (err) { console.error('Bay unassign failed:', err); }
  }, [localUpdateTruck]);

  return (
    <div className="smap-root">

      <div className="vtb">
        <div className="vtb-left">
          <div className="vtb-stat"><span className="vtb-stat-val">{baysOccupied}/{BAYS.length}</span><span className="vtb-stat-lbl">Bays Occupied</span></div>
          <div className="vtb-divider" />
          <div className="vtb-stat"><span className="vtb-stat-val">{placed.length}</span><span className="vtb-stat-lbl">In Bays</span></div>
          {unplaced.length > 0 && <>
            <div className="vtb-divider" />
            <div className="vtb-stat"><span className="vtb-stat-val" style={{ color: '#f59e0b' }}>{unplaced.length}</span><span className="vtb-stat-lbl">In Yard</span></div>
          </>}
        </div>
        <div className="vtb-right"><FullscreenBtn /></div>
      </div>

      <div className="smap-main">
        <YardStrip
          trucks={unplaced.slice(0, YARD_SLOTS)}  // up to 45
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDropFromBay={handleUnassign}
        />

        <div className="smap-canvas-wrap">
          {/* viewBox zoomed out for fit — landscape */}
          <svg viewBox="20 225 560 440" className="smap-svg" xmlns="http://www.w3.org/2000/svg">
            {/* 90° CCW: translate(0,900) rotate(-90) → point(x,y) maps to (y, 900-x) */}
            <g transform="translate(0, 900) rotate(-90)">

              {/* Aisle line between column 1 and column 2 */}
              <line x1="450" y1="44" x2="450" y2="244"
                stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="6 4" />

              {BAYS.map(bay => (
                <BayRect
                  key={bay.id}
                  bay={bay}
                  trucks={activeTrucks}
                  isSelected={selectedBay?.id === bay.id}
                  isDragOver={dragOverBayId === bay.id}
                  onSelect={b => setSelectedBay(prev => prev?.id === b.id ? null : b)}
                  onDragOver={(e) => handleBayDragOver(e, bay.id)}
                  onDragLeave={handleBayDragLeave}
                  onDrop={(e) => handleBayDrop(e, bay)}
                />
              ))}
            </g>
          </svg>
        </div>

        {selectedBay && (
          <BayPanel
            bay={selectedBay}
            trucks={activeTrucks}
            onClose={() => setSelectedBay(null)}
            onUnassign={handleUnassign}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          />
        )}
      </div>

    </div>
  );
}
